import { getModelOrThrow, getModelRelationOrThrow, expandJoinStrategySteps } from '../model/utils';
import { RelationWithBase, Field, Model, Relation } from '../types';

/**
 * Simplified helper to resolve only the joinKeyOnBase that does not need
 * related fields or relations. For full key resolution see resolveRelationKeys.
 */

export const resolveJoinKeyOnBase = (
  relation: RelationWithBase,
  baseFields: Field[],
  models: Model[],
): string | null => {
  const { joinStrategy } = relation;
  if (joinStrategy === undefined) {
    throw new Error(`Relation ${relation.key} has no join strategy`);
  }

  if ('sql' in joinStrategy) {
    // Return the first join key from the base and related keys to indicate success.
    // Many types need to be refactored if we want to support multiple join keys in relations.
    const keys = resolveRelationSqlBaseKeys(relation, baseFields);
    return keys !== null && keys.length > 0 ? keys[0] : null;
  }

  if ('steps' in joinStrategy) {
    const relationKey = joinStrategy.steps.at(0)?.relationKey;
    if (relationKey === undefined) {
      throw new Error(`Join strategy has no steps`);
    }
    // First step is a relation on the same model / base pipeline so resolve it from the same set of fields
    const model = getModelOrThrow(models, relation.baseModelId);
    return resolveJoinKeyOnBase(
      { ...getModelRelationOrThrow(relationKey, model), baseModelId: model.modelId },
      baseFields,
      models,
    );
  }

  return (
    findModelPropertyField(baseFields, relation.baseModelId, joinStrategy.joinKeyOnBase)?.key ??
    null
  );
};

/**
 * Attempt to resolve the join keys used in a relation's join strategy to actual field keys on the base and related pipeline states.
 * Use when we need to know which relations are available or which fields are being joined on.
 * Returns null when any of the join keys cannot be resolved.
 */

export const resolveRelationKeys = (
  relation: RelationWithBase,
  baseFields: Field[],
  relatedFields: Field[],
  relations: Relation[],
  models: Model[],
): { joinKeyOnBase: string; joinKeyOnRelated: string } | null => {
  const { joinStrategy } = relation;
  if (joinStrategy === undefined) {
    return null;
  }
  switch (true) {
    case 'steps' in joinStrategy: {
      const steps = resolveJoinStrategySteps(
        relation,
        baseFields,
        relatedFields,
        relations,
        models,
      );
      if (steps === null) {
        return null;
      }
      const firstStep = steps.at(0);
      const lastStep = steps.at(-1);
      if (firstStep === undefined || lastStep === undefined || firstStep === lastStep) {
        throw new Error(`Failed to resolve join strategy steps for relation ${relation.key}`);
      }
      return {
        joinKeyOnBase: firstStep.joinKeyOnBase,
        joinKeyOnRelated: lastStep.joinKeyOnRelated,
      };
    }
    case 'sql' in joinStrategy: {
      const allJoinKeys = resolveJoinStrategySQL(relation, baseFields, relatedFields);
      if (allJoinKeys === null) {
        return null;
      }
      // Return the first join key from the base and related keys to indicate success.
      // Many types need to be refactored if we want to support multiple join keys in relations.
      return {
        joinKeyOnBase: allJoinKeys.baseKeys[0],
        joinKeyOnRelated: allJoinKeys.relatedKeys[0],
      };
    }
    default:
      return resolveJoinStrategyDirect(relation, baseFields, relatedFields);
  }
};

/**
 * Attempt to resolve the join keys used in a JoinStrategyDirect-based join strategy to actual field keys on the base and related pipeline states.
 */
export const resolveJoinStrategyDirect = (
  relation: RelationWithBase,
  baseFields: Field[],
  relatedFields: Field[],
): { joinKeyOnBase: string; joinKeyOnRelated: string } | null => {
  const { joinStrategy } = relation;
  if (joinStrategy === undefined) {
    throw new Error(`Relation ${relation.key} has no join strategy`);
  }
  if (!('joinKeyOnBase' in joinStrategy) || !('joinKeyOnRelated' in joinStrategy)) {
    throw new Error(`Join strategy is not of type JoinStrategyDirect`);
  }

  const baseField = findModelPropertyField(
    baseFields,
    relation.baseModelId,
    joinStrategy.joinKeyOnBase,
  );
  const relatedField = findModelPropertyField(
    relatedFields,
    relation.modelId,
    joinStrategy.joinKeyOnRelated,
  );

  return baseField !== undefined && relatedField !== undefined
    ? {
        joinKeyOnBase: baseField.key,
        joinKeyOnRelated: relatedField.key,
      }
    : null;
};

/**
 * Attempt to resolve the join keys used in a JoinStrategySteps-based join strategy to actual field keys on the base and related pipeline states.
 * This returns an array of join keys for each step in the strategy.
 * The join keys that are resolved to actual fields are on the first and last step.
 * Intermediary steps traverse models and so their join keys are assumed to be correct.
 */
export const resolveJoinStrategySteps = (
  relation: RelationWithBase,
  baseFields: Field[],
  relatedFields: Field[],
  relations: Relation[],
  models: Model[],
): { joinKeyOnBase: string; joinKeyOnRelated: string }[] | null => {
  const { joinStrategy } = relation;
  if (joinStrategy === undefined) {
    throw new Error(`Relation ${relation.key} has no join strategy`);
  }
  if (!('steps' in joinStrategy)) {
    throw new Error(`Join strategy is not of type JoinStrategySteps`);
  }

  const steps = expandJoinStrategySteps(joinStrategy, relations, models);
  const firstStep = steps.at(0);
  const lastStep = steps.at(-1);
  if (firstStep === undefined || lastStep === undefined) {
    throw new Error(`Relation ${relation.key} has no join keys`);
  }

  const baseField = findModelPropertyField(
    baseFields,
    relation.baseModelId,
    firstStep.joinKeyOnBase,
  );
  const relatedField = findModelPropertyField(
    relatedFields,
    relation.modelId,
    lastStep.joinKeyOnRelated,
  );

  if (baseField === undefined || relatedField === undefined) {
    return null;
  }

  return [
    {
      ...firstStep,
      joinKeyOnBase: baseField.key,
    },
    ...steps.slice(1, -1),
    {
      ...lastStep,
      joinKeyOnRelated: relatedField.key,
    },
  ];
};

/**
 * Attempt to resolve the join keys used in a SQL-based join strategy to actual field keys on the base and related pipeline states.
 * If any of the join keys cannot be resolved to an actual field, this returns null since we can't be sure if the join is valid or not.
 */
export const resolveJoinStrategySQL = (
  relation: RelationWithBase,
  baseFields: Field[],
  relatedFields: Field[],
): { baseKeys: string[]; relatedKeys: string[] } | null => {
  const { joinStrategy } = relation;
  if (joinStrategy === undefined) {
    throw new Error(`Relation ${relation.key} has no join strategy`);
  }
  if (!('sql' in joinStrategy)) {
    throw new Error(`Join strategy is not of type JoinStrategySQL`);
  }

  const baseKeys = resolveRelationSqlBaseKeys(relation, baseFields);
  const relatedKeys = resolveRelationSqlRelatedKeys(relation, relatedFields);

  if (baseKeys === null || relatedKeys === null) {
    return null;
  }

  return { baseKeys, relatedKeys };
};

export const resolveRelationSqlBaseKeys = (
  relation: RelationWithBase,
  baseFields: Field[],
): string[] | null => {
  const { joinStrategy } = relation;
  if (joinStrategy === undefined) {
    throw new Error(`Relation ${relation.key} has no join strategy`);
  }
  if (!('sql' in joinStrategy)) {
    throw new Error(`Join strategy is not of type JoinStrategySQL`);
  }

  const baseKeys = Array.from(joinStrategy.sql.matchAll(/{{base_model}}\.[`"]?(\w+)[`"]?/g)).map(
    (match) => match[1],
  );
  if (baseKeys.length === 0) {
    return null;
  }
  const baseFieldsResolved = baseKeys.map((key) =>
    findModelPropertyField(baseFields, relation.baseModelId, key),
  );
  if (baseFieldsResolved.some((field) => field === undefined)) {
    return null;
  }
  return baseFieldsResolved.map((field) => field!.key);
};

export const resolveRelationSqlRelatedKeys = (
  relation: RelationWithBase,
  relatedFields: Field[],
): string[] | null => {
  const { joinStrategy } = relation;
  if (joinStrategy === undefined) {
    throw new Error(`Relation ${relation.key} has no join strategy`);
  }
  if (!('sql' in joinStrategy)) {
    throw new Error(`Join strategy is not of type JoinStrategySQL`);
  }

  const relatedKeys = Array.from(
    joinStrategy.sql.matchAll(/{{related_model}}\.[`"]?(\w+)[`"]?/g),
  ).map((match) => match[1]);
  if (relatedKeys.length === 0) {
    return null;
  }
  const relatedFieldsResolved = relatedKeys.map((key) =>
    findModelPropertyField(relatedFields, relation.modelId, key),
  );
  if (relatedFieldsResolved.some((field) => field === undefined)) {
    return null;
  }
  return relatedFieldsResolved.map((field) => field!.key);
};

/**
 * Find the (possibly renamed) field that is the given property of the given model.
 * State relations are calculated purely on model properties to avoid flooding the state with
 * relations due to matching field names.
 */
export const findModelPropertyField = (fields: Field[], modelId: string, propertyKey: string) =>
  fields.find(
    (field) => field.model?.modelId === modelId && field.model?.propertyKey === propertyKey,
  );

/**
 * Find the field suitable for joining a custom join strategy.
 * Only match by key since the join keys are explicitly picked.
 */
export const resolveCustomJoinKey = (fields: Field[], joinKey: string) =>
  fields.find((field) => field.key === joinKey);
